// AgentCommissionMaster.jsx

import React, { useEffect, useState } from "react";
import "../assets/AgentCommissionMaster.css";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { fetchEmployeeReport } from "../redux/slices/employeeReportSlice";
import { getBranches } from "../redux/slices/branchSlice";
import AgentCommissionMasterEditModal from "../modals/AgentCommissionMasterEditModal";

const API = "https://payyug.in/api";

const commissionRows = [
  { key: "loanApplied",    label: "Loan Applied" },
  { key: "loanDisbursed",  label: "Loan Disbursed" },
  { key: "emiCollection",  label: "EMI Collection" },
  { key: "saving",         label: "Saving" },
  { key: "rd",             label: "RD" },
  { key: "dd",             label: "DD" },
  { key: "fd",             label: "FD" },
  { key: "mis",            label: "MIS" },
];

const initialCommissionData = {
  loanApplied:   { checked: false, type: "percentage", value: "" },
  loanDisbursed: { checked: false, type: "percentage", value: "" },
  emiCollection: { checked: false, type: "fixed",      value: "" },
  saving:        { checked: false, type: "percentage", value: "" },
  rd:            { checked: false, type: "fixed",      value: "" },
  dd:            { checked: false, type: "percentage", value: "" },
  fd:            { checked: false, type: "fixed",      value: "" },
  mis:           { checked: false, type: "percentage", value: "" },
};

const AgentCommissionMaster = () => {
  const dispatch = useDispatch();

  const pageSize    = 1000;
  const superAdminid = localStorage.getItem("adminuserId") || "14";

  const [selectedBranch,   setSelectedBranch]   = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [list,             setList]             = useState([]);
  const [commissionData,   setCommissionData]   = useState(initialCommissionData);

  // modal
  const [isModalOpen,  setIsModalOpen]  = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const { list: employees = [] } = useSelector((state) => state.employeeReport);
  const { branches = [] }        = useSelector((state) => state.branch);

  useEffect(() => {
    dispatch(fetchEmployeeReport({ page: 1, pageSize }));
    dispatch(getBranches({ page: 1, pageSize }));
    fetchData();
  }, [dispatch]);

  // ── fetch all records ─────────────────────────────────────────────────────
  const fetchData = async () => {
    try {
      const res  = await axios.get(`${API}/get-all-agent-commison-master`);
      const data = res.data?.data || res.data?.result || res.data || [];
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setList([]);
    }
  };

  // ── filter employees by branch ────────────────────────────────────────────
  const filteredEmployees = selectedBranch
    ? employees.filter((emp) => String(emp.branchId) === String(selectedBranch))
    : employees;

  // ── get employee name by id ───────────────────────────────────────────────
  const getEmployeeName = (id) => {
    const emp = employees.find((e) => String(e.id) === String(id));
    return emp?.fullName || id || "-";
  };

  // ── commission field change ───────────────────────────────────────────────
  const handleCommissionChange = (key, field, value) => {
    setCommissionData((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const safeNumber = (val) =>
    val === "" || val === null || val === undefined ? 0 : Number(val);

  // ── save (checkbox triggers save) ────────────────────────────────────────
  const handleSave = async (updatedData) => {
    if (!selectedBranch || !selectedEmployee) {
      alert("Please select branch & employee");
      return;
    }

    const cd = updatedData || commissionData;

    const payload = {
      superAdminid,
      branchId:    selectedBranch,
      employeesId: selectedEmployee,

      loanAppliedValue:    cd.loanApplied.checked   ? safeNumber(cd.loanApplied.value)   : 0,
      loanAppliedType:     cd.loanApplied.type,
      loanDisbursedValue:  cd.loanDisbursed.checked ? safeNumber(cd.loanDisbursed.value) : 0,
      loanDisbursedType:   cd.loanDisbursed.type,
      emiCollectionValue:  cd.emiCollection.checked ? safeNumber(cd.emiCollection.value) : 0,
      emiCollectionType:   cd.emiCollection.type,
      savingValue:         cd.saving.checked        ? safeNumber(cd.saving.value)        : 0,
      savingType:          cd.saving.type,
      rdValue:             cd.rd.checked            ? safeNumber(cd.rd.value)            : 0,
      rdType:              cd.rd.type,
      ddValue:             cd.dd.checked            ? safeNumber(cd.dd.value)            : 0,
      ddType:              cd.dd.type,
      fdValue:             cd.fd.checked            ? safeNumber(cd.fd.value)            : 0,
      fdType:              cd.fd.type,
      misValue:            cd.mis.checked           ? safeNumber(cd.mis.value)           : 0,
      misType:             cd.mis.type,
      status: "active",
    };

    try {
      await axios.post(`${API}/create-agent-commison-master`, payload);
      fetchData();
      alert("Saved Successfully");
    } catch (err) {
      console.error("API ERROR", err.response?.data || err.message);
      alert("Something went wrong");
    }
  };

  // ── delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await axios.delete(`${API}/delete-agent-commison-master/${id}`);
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  // ── format value for table cell ───────────────────────────────────────────
  const formatValue = (value, type) => {
    if (!value || value === 0) return null; // null = hide column
    return type === "percentage" ? `${value}%` : `${value} (Fix)`;
  };

  // ── which columns have non-zero data (to show in table header) ───────────
  const activeColumns = commissionRows.filter((row) =>
    list.some((r) => {
      const val = r[`${row.key}Value`];
      return val && Number(val) > 0;
    })
  );

  return (
    <div className="agentCommission-container">

      <h1 className="agentCommission-title">Agent Commission Master</h1>

      <div className="agentCommission-card">

        {/* ── Branch + Employee ── */}
        <div className="agentCommission-top">
          <div className="agentCommission-field">
            <label>Select Branch</label>
            <select
              value={selectedBranch}
              onChange={(e) => {
                setSelectedBranch(e.target.value);
                setSelectedEmployee("");
              }}
            >
              <option value="">Select Branch</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>

          <div className="agentCommission-field">
            <label>Select Employee</label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
            >
              <option value="">Select Employee</option>
              {filteredEmployees.map((emp) => (
                <option key={emp.id} value={emp.id}>{emp.fullName}</option>
              ))}
            </select>
          </div>
        </div>

        {/* ── Commission Rows ── */}
        <div className="commissionRows">
          {commissionRows.map((row) => (
            <div className="commissionRow" key={row.key}>

              {/* Label */}
              <div className="commissionLeft">
                <h4>{row.label}</h4>
              </div>

              {/* Type dropdown */}
              <select
                value={commissionData[row.key].type}
                onChange={(e) =>
                  handleCommissionChange(row.key, "type", e.target.value)
                }
              >
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed</option>
              </select>

              {/* Value input */}
              <div className="valueBox">
                <input
                  type="number"
                  placeholder="0"
                  value={commissionData[row.key].value}
                  onChange={(e) =>
                    handleCommissionChange(row.key, "value", e.target.value)
                  }
                />
                <span>
                  {commissionData[row.key].type === "percentage" ? "%" : "FIX"}
                </span>
              </div>

              {/* Checkbox — triggers save */}
              <input
                type="checkbox"
                checked={commissionData[row.key].checked}
                onChange={(e) => {
                  const newData = {
                    ...commissionData,
                    [row.key]: {
                      ...commissionData[row.key],
                      checked: e.target.checked,
                    },
                  };
                  setCommissionData(newData);
                  setTimeout(() => handleSave(newData), 100);
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="agentCommission-tableWrapper">
        <table className="agentCommission-table">
          <thead>
            <tr>
              <th>SN</th>
              <th>Employee</th>
              {/* ← only show columns that have data */}
              {activeColumns.map((col) => (
                <th key={col.key}>{col.label}</th>
              ))}
              <th>Update</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td colSpan={3 + activeColumns.length} style={{ textAlign: "center", padding: "20px" }}>
                  No records found
                </td>
              </tr>
            ) : (
              list.map((item, i) => (
                <tr key={item.id}>
                  <td>{i + 1}</td>
                  {/* ← show employee name */}
                  <td>{getEmployeeName(item.employeesId)}</td>
                  {/* ← only show active columns */}
                  {activeColumns.map((col) => {
                    const val  = item[`${col.key}Value`];
                    const type = item[`${col.key}Type`];
                    const formatted = formatValue(val, type);
                    return (
                      <td key={col.key}>
                        {formatted || "-"}
                      </td>
                    );
                  })}
                  <td>
                    <button
                      className="editBtn"
                      onClick={() => {
                        setSelectedItem(item);
                        setIsModalOpen(true);
                      }}
                    >
                      Edit
                    </button>
                  </td>
                  <td>
                    <button
                      className="deleteBtn"
                      onClick={() => handleDelete(item.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Edit Modal ── */}
      <AgentCommissionMasterEditModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        item={selectedItem}
        onUpdated={fetchData}
      />
    </div>
  );
};

export default AgentCommissionMaster;